import { html, nothing } from 'lit';

import { getEnvDotColor } from '../../utils/get-env-dot-color';
import {
  splitVisibleSolutionGroups,
  type SolutionChannelGroup,
} from '../../utils/group-channels-by-solution';
import type { PlatformChannel } from '../../utils/map-connection-to-row';
import { stopRowKeyActivation } from '../../utils/row-key-activation';
import {
  buildSolutionGroupViews,
  buildSolutionsToggleView,
  type SolutionGroupsTestIdPrefix,
  type SolutionGroupsTranslate,
} from './shared';
import { SOLUTION_CHANNEL_GROUPS_CSS } from './styles';

export interface RenderSolutionChannelGroupsArgs {
  groups: SolutionChannelGroup[];
  /** Scopes the expanded state, see `groupToggleKey`. */
  ownerId: string;
  isExpanded: (key: string) => boolean;
  onToggle: (key: string) => void;
  onGoToChannel: (channel: PlatformChannel) => void;
  onGoToSolution: (solutionId: string) => void;
  testIdPrefix: SolutionGroupsTestIdPrefix;
  /** Plain function, not a hook: this renderer runs inside a table cell. */
  translate: SolutionGroupsTranslate;
  /**
   * Only the Live Agent Platforms table passes this: it caps the boxes of a
   * connection and draws the "See N more solutions" button after them. Without
   * it every box is drawn and no such button, as before. The React twin used by
   * the Edit modal does not read it at all.
   */
  connectionToggle?: { expanded: boolean; onToggle: () => void };
}

/** A click on our button is the button's, never the row's selection toggle. */
const ownClick = (action: () => void) => (e: Event) => {
  e.stopPropagation();
  action();
};

/**
 * Lit renderer of the solution boxes for the table cell. The React twin used by
 * the Edit modal is `SolutionChannelGroups`; both map the same view model
 * (`buildSolutionGroupViews`) and inject the same stylesheet. Only this one
 * stops propagation, because only it renders inside a `ps-data-table` row.
 */
export const renderSolutionChannelGroups = ({
  groups,
  ownerId,
  isExpanded,
  onToggle,
  onGoToChannel,
  onGoToSolution,
  testIdPrefix,
  translate,
  connectionToggle,
}: RenderSolutionChannelGroupsArgs) => {
  const { visible, remaining } = connectionToggle
    ? splitVisibleSolutionGroups(groups, connectionToggle.expanded)
    : { visible: groups, remaining: 0 };
  const solutionsToggle = connectionToggle
    ? buildSolutionsToggleView({
        remaining,
        expanded: connectionToggle.expanded,
        translate,
      })
    : null;
  return html`
    <style>
      ${SOLUTION_CHANNEL_GROUPS_CSS}
    </style>
    ${buildSolutionGroupViews({
      groups: visible,
      ownerId,
      isExpanded,
      testIdPrefix,
      translate,
    }).map(
      ({ link, ...view }) =>
        html`<div
          class="c-solution-group"
          role=${view.regionLabel == null ? nothing : 'region'}
          aria-label=${view.regionLabel ?? nothing}
          test-id=${view.testId}
        >
          ${view.unresolved
            ? nothing
            : link
              ? html`<ps-tooltip content=${link.tooltip}>
                  <button
                    type="button"
                    class="c-solution-group__chip"
                    aria-label=${link.ariaLabel}
                    @keydown=${stopRowKeyActivation}
                    @click=${ownClick(() => onGoToSolution(link.solutionId))}
                  >
                    <span class="c-solution-group__label" title=${view.name}
                      >${view.name}</span
                    >
                    <ps-icon name="arrow-in-circle" size="xsmall"></ps-icon>
                  </button>
                </ps-tooltip>`
              : html`<span
                  class="c-solution-group__chip c-solution-group__chip--static"
                >
                  <span class="c-solution-group__label" title=${view.name}
                    >${view.name}</span
                  >
                </span>`}
          ${view.pills.map((pill) => {
            const content = html`<span
                class="c-solution-group__dot"
                style=${`background:${getEnvDotColor(pill.channel.env)}`}
              ></span>
              <span class="c-solution-group__label" title=${pill.name}
                >${pill.name}</span
              >`;
            // A pill that leads nowhere is text, named by its visible name: no
            // button, tooltip or arrow promising a page (see ChannelPillView).
            if (!pill.interactive) {
              return html`<span
                class="c-solution-group__pill c-solution-group__pill--static"
                test-id=${pill.testId}
                >${content}</span
              >`;
            }
            return html`<ps-tooltip content=${pill.tooltip}>
              <button
                type="button"
                class="c-solution-group__pill"
                test-id=${pill.testId}
                aria-label=${pill.ariaLabel}
                @keydown=${stopRowKeyActivation}
                @click=${ownClick(() => onGoToChannel(pill.channel))}
              >
                ${content}
                <ps-icon name="arrow-in-circle" size="xsmall"></ps-icon>
              </button>
            </ps-tooltip>`;
          })}
          ${view.more
            ? html`<button
                type="button"
                class="c-solution-group__more"
                aria-expanded=${view.more.expanded ? 'true' : 'false'}
                @keydown=${stopRowKeyActivation}
                @click=${ownClick(() => onToggle(view.key))}
              >
                ${view.more.label}
                <ps-icon name=${view.more.icon} size="xsmall"></ps-icon>
              </button>`
            : nothing}
        </div>`
    )}
    ${connectionToggle && solutionsToggle
      ? html`<div class="c-solution-groups__toggle">
          <button
            type="button"
            class="c-solution-group__more"
            test-id=${`${testIdPrefix}-solutions-toggle-${ownerId}`}
            aria-expanded=${solutionsToggle.expanded ? 'true' : 'false'}
            @keydown=${stopRowKeyActivation}
            @click=${ownClick(connectionToggle.onToggle)}
          >
            ${solutionsToggle.label}
            <ps-icon name=${solutionsToggle.icon} size="xsmall"></ps-icon>
          </button>
        </div>`
      : nothing}
  `;
};
